import { useCurrentUser } from '@a6tr/domains/authentication/hooks/useCurrentUser';
import { useLogout } from '@a6tr/domains/authentication/hooks/useLogout';
import { LOGIN_PATH } from '@a6tr/domains/authentication/login';
import { UserKind } from '@a6tr/gen/user/v1/user_pb';
import { useLocation } from '@tanstack/react-router';
import { Button, LoaderSpinner } from '@thetreep/travel-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// Holds the page until the session is confirmed, and only lets platform users through. The API
// enforces the same rule on every back-office procedure; this gate only avoids showing screens
// whose calls would all be refused.
export const AuthGate = ({ children }: { children: ReactNode }) => {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const { user, isError, refetch } = useCurrentUser();
	const { logout, isPending } = useLogout();

	if (pathname === LOGIN_PATH) {
		return children;
	}

	if (user?.kind === UserKind.PLATFORM) {
		return children;
	}

	if (user) {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4 text-center">
				<h1 className="typography-heading-4">
					{t('auth.gate.forbiddenTitle')}
				</h1>
				<p className="max-w-md typography-body-m">{t('auth.gate.forbidden')}</p>
				<Button
					variant="secondary"
					size="m"
					isLoading={isPending}
					onPress={logout}
				>
					{t('auth.logout')}
				</Button>
			</div>
		);
	}

	if (isError) {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center gap-4">
				<p className="typography-body-m">{t('auth.gate.error')}</p>
				<Button variant="primary" size="m" onPress={() => void refetch()}>
					{t('auth.gate.retry')}
				</Button>
			</div>
		);
	}

	return (
		<div className="flex min-h-screen items-center justify-center">
			<LoaderSpinner />
		</div>
	);
};
